import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { toPng } from "html-to-image";
import SlidingPlayMain from "../../../admin/story/SlidingPlayMain";
import { markScreenshotNotReady, signalScreenshotSceneReady } from "../../../lib/screenshotReady";
import ScreenshotExpiredPage from "../components/ScreenshotExpiredPage";
import {
    getImageUrl,
    getTemplateGrid,
    getTemplateStartingLayout,
    loadScreenshotSliding,
} from "../lib/screenshotApi";
import { isScreenshotAccessExpired } from "../lib/screenshotAccess";
import { APP_ICON_PATH } from "../routes";
import { getScreenshotScene, isScreenshotSceneConfigured } from "../screenshotScenes";

/** App Store / Play Store icon canvas. */
export const APP_ICON_SIZE = 512;

const SAVE_BAR_H = 64;
const APP_ICON_FILE_NAME = "sliding-puzzle-quest-app-icon-512x512.png";

/** Same Funny kitchen / waffle puzzle as `/screenshot/05` and the feature graphic. */
const APP_ICON_SCENE_SLUG = "05";

const ICON_GRID = 3;
const ICON_EMPTY = ICON_GRID * ICON_GRID - 1;

/**
 * 3×3 icon board from the story image.
 * Waffle face sits in the center; empty slot immediately to its left (just after a slide).
 */
function buildIconStartingLayout() {
    // Slice 1 ≈ top-center of image (smiling waffle face on kitchen art).
    const waffle = 1;
    return [
        0, 2, 3,
        ICON_EMPTY, waffle, 5,
        6, 7, 4,
    ];
}

/** Floating center-face tile — tilted neon piece with the waffle face. */
function WaffleFaceHeroTile({ imageUrl }) {
    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                left: "50%",
                top: "46%",
                width: 210,
                height: 210,
                marginLeft: "-105px",
                marginTop: "-105px",
                borderRadius: "14px",
                overflow: "hidden",
                transform: "rotate(-14deg) scale(1.12)",
                zIndex: 8,
                pointerEvents: "none",
                // Zoom into the smiling waffle-face region of the kitchen art
                backgroundImage: `url("${imageUrl}")`,
                backgroundSize: "210% 210%",
                backgroundPosition: "52% 12%",
                backgroundRepeat: "no-repeat",
                boxShadow: `
                    0 0 0 3px rgba(56, 189, 248, 0.95),
                    0 0 22px rgba(14, 165, 233, 0.9),
                    0 0 36px rgba(251, 191, 36, 0.45),
                    0 13px 24px rgba(0, 0, 0, 0.55)
                `,
            }}
        />
    );
}

const DUST_PARTICLES = [
    { top: "38%", left: "42%", size: 4, color: "#38bdf8", delay: "0s" },
    { top: "44%", left: "48%", size: 3, color: "#fbbf24", delay: "0.3s" },
    { top: "52%", left: "40%", size: 5, color: "#7dd3fc", delay: "0.7s" },
    { top: "48%", left: "55%", size: 3, color: "#fcd34d", delay: "1.1s" },
    { top: "36%", left: "58%", size: 2, color: "#38bdf8", delay: "0.5s" },
    { top: "58%", left: "50%", size: 3, color: "#93c5fd", delay: "1.4s" },
    { top: "41%", left: "36%", size: 2, color: "#fde68a", delay: "0.9s" },
    { top: "55%", left: "60%", size: 4, color: "#22d3ee", delay: "0.2s" },
];

/**
 * Square 512×512 app icon capture — same puzzle image as screenshot/05.
 * Focused 3×3 neon board, empty left of waffle tile, no UI chrome.
 */
export default function AppIconPage() {
    const scene = getScreenshotScene(APP_ICON_SCENE_SLUG);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [group, setGroup] = useState(null);
    const [previewScale, setPreviewScale] = useState(1);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const frameRef = useRef(null);

    const expired = isScreenshotAccessExpired();
    const iconLayout = useMemo(() => buildIconStartingLayout(), []);

    useEffect(() => {
        if (expired) {
            setLoading(false);
            return undefined;
        }
        if (!scene || !isScreenshotSceneConfigured(scene)) {
            setError("App icon scene is not configured");
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await loadScreenshotSliding(scene);
                if (cancelled) return;
                setGroup(result.group);
                // Confirm template loads (same puzzle); icon uses 3×3 crop of the story image.
                getTemplateGrid(result.template);
                getTemplateStartingLayout(result.template);
            } catch (e) {
                if (!cancelled) setError(e?.message || "Failed to load puzzle");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [expired, scene]);

    const imageUrl = useMemo(() => getImageUrl(group?.story), [group]);

    useEffect(() => {
        if (expired || loading || error || !imageUrl) {
            markScreenshotNotReady();
            return undefined;
        }
        signalScreenshotSceneReady({
            path: APP_ICON_PATH,
            slug: "app-icon",
        });
        return undefined;
    }, [expired, loading, error, imageUrl]);

    useEffect(() => {
        const updateScale = () => {
            const padding = 32;
            const availableW = window.innerWidth - padding;
            const availableH = window.innerHeight - SAVE_BAR_H - padding;
            setPreviewScale(
                Math.min(1, availableW / APP_ICON_SIZE, availableH / APP_ICON_SIZE),
            );
        };
        updateScale();
        window.addEventListener("resize", updateScale);
        return () => window.removeEventListener("resize", updateScale);
    }, []);

    useEffect(() => {
        const prevHtml = document.documentElement.style.overflow;
        const prevBody = document.body.style.overflow;
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        return () => {
            document.documentElement.style.overflow = prevHtml;
            document.body.style.overflow = prevBody;
        };
    }, []);

    const handleSave = useCallback(async () => {
        const node = frameRef.current;
        if (!node || saving) return;

        setSaving(true);
        setSaveError(null);
        const previousTransform = node.style.transform;
        node.style.transform = "none";

        try {
            await new Promise((resolve) =>
                requestAnimationFrame(() => requestAnimationFrame(resolve)),
            );
            const dataUrl = await toPng(node, {
                width: APP_ICON_SIZE,
                height: APP_ICON_SIZE,
                pixelRatio: 1,
                cacheBust: true,
                skipFonts: true,
                style: {
                    transform: "none",
                    transformOrigin: "top left",
                },
            });
            const link = document.createElement("a");
            link.download = APP_ICON_FILE_NAME;
            link.href = dataUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (e) {
            setSaveError(e?.message || "Failed to save image");
        } finally {
            node.style.transform = previousTransform;
            setSaving(false);
        }
    }, [saving]);

    if (expired) {
        return <ScreenshotExpiredPage />;
    }

    const ready = Boolean(imageUrl);

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#020617",
                overflow: "hidden",
                zIndex: 1,
                gap: 1.5,
                pb: `${SAVE_BAR_H}px`,
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    width: Math.round(APP_ICON_SIZE * previewScale),
                    height: Math.round(APP_ICON_SIZE * previewScale),
                    position: "relative",
                    overflow: "hidden",
                    flexShrink: 0,
                }}
            >
                <Box
                    ref={frameRef}
                    data-screenshot-frame="sliding-app-icon"
                    sx={{
                        width: APP_ICON_SIZE,
                        height: APP_ICON_SIZE,
                        transform: `scale(${previewScale})`,
                        transformOrigin: "top left",
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: 0,
                        bgcolor: "#020617",
                    }}
                >
                    {/* Deep blue cinematic backdrop */}
                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            inset: 0,
                            background: `
                                radial-gradient(circle at 55% 45%, rgba(14, 165, 233, 0.22) 0%, transparent 42%),
                                radial-gradient(circle at 40% 55%, rgba(251, 191, 36, 0.12) 0%, transparent 35%),
                                radial-gradient(circle at 50% 50%, #0c4a6e 0%, #020617 62%, #000 100%)
                            `,
                        }}
                    />

                    {loading ? (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <CircularProgress size={40} sx={{ color: "#38bdf8" }} />
                        </Box>
                    ) : error ? (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 4,
                            }}
                        >
                            <Typography color="error" align="center">
                                {error}
                            </Typography>
                        </Box>
                    ) : ready ? (
                        <>
                            {/* Neon board — fills the full 512×512 canvas */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 2,
                                }}
                            >
                                <Box
                                    aria-hidden
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        background:
                                            "radial-gradient(circle at 50% 45%, rgba(56,189,248,0.28) 0%, rgba(14,165,233,0.08) 42%, transparent 68%)",
                                        zIndex: 0,
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: "relative",
                                        width: "100%",
                                        height: "100%",
                                        p: 0,
                                        boxSizing: "border-box",
                                        zIndex: 1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            overflow: "hidden",
                                            bgcolor: "#020617",
                                        }}
                                    >
                                        <SlidingPlayMain
                                            imageUrl={imageUrl}
                                            gridSize={ICON_GRID}
                                            startingLayout={iconLayout}
                                            hideCompletedCaption
                                            fillParent
                                            readOnly
                                            playMoveSound={false}
                                            showMatchCelebrations={false}
                                            gridGutterColor="#0284c7"
                                            gridBorderColor="#38bdf8"
                                        />
                                    </Box>
                                </Box>

                                {/* Center hero — waffle face tile (fills the middle focal point) */}
                                <WaffleFaceHeroTile imageUrl={imageUrl} />

                                {/* Subtle motion streak near the empty / waffle slide */}
                                <Box
                                    aria-hidden
                                    sx={{
                                        position: "absolute",
                                        top: "48%",
                                        left: "18%",
                                        width: "48%",
                                        height: 18,
                                        borderRadius: 999,
                                        background:
                                            "linear-gradient(90deg, transparent 0%, rgba(56,189,248,0.15) 25%, rgba(251,191,36,0.35) 55%, rgba(56,189,248,0.2) 80%, transparent 100%)",
                                        filter: "blur(6px)",
                                        transform: "translateY(-50%)",
                                        zIndex: 3,
                                        pointerEvents: "none",
                                    }}
                                />
                            </Box>

                            {/* Blue + gold dust particles */}
                            <Box
                                aria-hidden
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    pointerEvents: "none",
                                    zIndex: 6,
                                    "@keyframes iconDust": {
                                        "0%, 100%": { opacity: 0.2, transform: "translateY(0) scale(0.8)" },
                                        "50%": { opacity: 1, transform: "translateY(-8px) scale(1.2)" },
                                    },
                                }}
                            >
                                {DUST_PARTICLES.map((p, i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            position: "absolute",
                                            top: p.top,
                                            left: p.left,
                                            width: p.size,
                                            height: p.size,
                                            borderRadius: "50%",
                                            bgcolor: p.color,
                                            boxShadow: `0 0 ${p.size * 3}px ${p.color}`,
                                            animation: `iconDust ${2.2 + (i % 3) * 0.35}s ease-in-out ${p.delay} infinite`,
                                        }}
                                    />
                                ))}
                            </Box>
                        </>
                    ) : (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography color="text.secondary">Puzzle image missing.</Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            <Box
                sx={{
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: SAVE_BAR_H,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    bgcolor: "rgba(2, 6, 23, 0.92)",
                    borderTop: "1px solid rgba(148, 163, 184, 0.2)",
                    zIndex: 20,
                    px: 2,
                    boxSizing: "border-box",
                }}
            >
                <Button
                    variant="contained"
                    size="medium"
                    startIcon={
                        saving ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : (
                            <SaveAltIcon />
                        )
                    }
                    onClick={handleSave}
                    disabled={!ready || saving || loading}
                    sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        px: 3,
                        bgcolor: "#0ea5e9",
                        "&:hover": { bgcolor: "#0284c7" },
                    }}
                >
                    {saving ? "Saving…" : "Save 512×512 PNG"}
                </Button>
                {saveError ? (
                    <Typography variant="caption" color="error" sx={{ lineHeight: 1.2 }}>
                        {saveError}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
}
